<template>
  <div class="users">
    <header class="head">
      <div>
        <h1>Users</h1>
        <p class="muted">
          Admins are not assigned to a college. Approve a student against a workshop date. That
          college+date session is the only one they can see and vote in.
        </p>
      </div>
    </header>

    <p v-if="loading" class="muted">Loading…</p>
    <p v-else-if="error" class="error">{{ error }}</p>
    <template v-else>
      <section class="block">
        <h2>Admins</h2>
        <table class="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Admin</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="u in admins" :key="u.sub || u.email">
              <td>{{ u.name || '—' }}</td>
              <td>{{ u.email }}</td>
              <td><span class="role yes">Yes</span></td>
            </tr>
            <tr v-if="!admins.length">
              <td colspan="3" class="muted">No admins yet.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section class="block">
        <div class="block-head">
          <h2>Users</h2>
          <div class="filters">
            <button
              v-for="s in statuses"
              :key="s"
              class="btn"
              :class="filter === s ? 'btn-primary' : 'btn-secondary'"
              @click="setFilter(s)"
            >
              {{ s }}
            </button>
          </div>
        </div>
        <table class="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Admin</th>
              <th>College</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="u in filteredStudents" :key="u.sub">
              <td>{{ u.name }}</td>
              <td>{{ u.email }}</td>
              <td><span class="role no">No</span></td>
              <td class="college">
                <template v-if="u.collegeName || u.requestedCollegeName">
                  {{ u.collegeName || u.requestedCollegeName }}
                  <span v-if="isEverydayUser(u)" class="muted"> · every day</span>
                  <span v-else-if="u.workshopDate" class="muted"> · {{ u.workshopDate }}</span>
                  <span v-else-if="u.requestedCollegeName && !u.collegeName" class="muted">
                    · requested
                  </span>
                </template>
                <span v-else class="muted">—</span>
              </td>
              <td><span class="status" :data-status="u.status">{{ u.status }}</span></td>
              <td class="row-actions">
                <template v-if="u.status !== 'approved'">
                  <span v-if="isEverydayUser(u)" class="muted">Every day</span>
                  <select
                    v-else
                    v-model="approveDate[u.sub]"
                    class="date-select"
                    :aria-label="`Workshop date for ${u.name || u.email}`"
                  >
                    <option value="" disabled>Workshop date</option>
                    <option
                      v-for="s in sessionsFor(u)"
                      :key="s.id"
                      :value="s.workshopDate"
                    >
                      {{ formatDate(s.workshopDate) }}
                    </option>
                  </select>
                  <button
                    class="btn btn-primary"
                    :disabled="!canApprove(u)"
                    @click="approve(u)"
                  >
                    Approve
                  </button>
                </template>
                <button
                  v-if="u.status !== 'removed'"
                  class="btn btn-danger"
                  @click="remove(u.sub)"
                >
                  Remove
                </button>
              </td>
            </tr>
            <tr v-if="!filteredStudents.length">
              <td colspan="6" class="muted">No users with this status.</td>
            </tr>
          </tbody>
        </table>
      </section>
    </template>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue'
import { isAdminTestUniversity } from '../services/adminTestUniversity'
import { listUsers, approveUser, removeUser, adminListColleges } from '../services/api'

const statuses = ['pending', 'approved', 'removed']
const filter = ref('pending')
const users = ref([])
const sessions = ref([])
const approveDate = ref({})
const loading = ref(true)
const error = ref('')

const admins = computed(() =>
  users.value
    .filter((u) => u.isAdmin)
    .sort((a, b) => (a.email || '').localeCompare(b.email || '')),
)

const students = computed(() => users.value.filter((u) => !u.isAdmin))

const filteredStudents = computed(() =>
  students.value.filter((u) => u.status === filter.value),
)

function formatDate(iso) {
  if (!iso) return ''
  try {
    return new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}

function isEverydayUser(user) {
  return isAdminTestUniversity(user.collegeName || user.requestedCollegeName)
}

function sessionsFor(user) {
  const name = (user.collegeName || user.requestedCollegeName || '').trim().toLowerCase()
  if (!name) return sessions.value.filter((s) => s.workshopDate || s.everyday)
  return sessions.value.filter(
    (s) =>
      (s.name || '').toLowerCase() === name && (s.workshopDate || s.everyday),
  )
}

function canApprove(user) {
  if (isEverydayUser(user)) return true
  return !!approveDate.value[user.sub] && sessionsFor(user).length > 0
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const [res, collegeRes] = await Promise.all([
      listUsers({ status: 'all' }),
      adminListColleges().catch(() => ({ colleges: [] })),
    ])
    users.value = res.users || []
    sessions.value = collegeRes.colleges || []
  } catch (e) {
    error.value = e.message || 'Failed to load users'
  } finally {
    loading.value = false
  }
}

function setFilter(s) {
  filter.value = s
}

async function approve(user) {
  const workshopDate = isEverydayUser(user) ? undefined : approveDate.value[user.sub]
  if (!isEverydayUser(user) && !workshopDate) return
  try {
    await approveUser(user.sub, {
      collegeName: user.collegeName || user.requestedCollegeName,
      workshopDate,
    })
    await load()
  } catch (e) {
    error.value = e.message || 'Could not approve'
  }
}

async function remove(sub) {
  await removeUser(sub)
  await load()
}

onMounted(load)
</script>

<style scoped>
.head {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

h1 {
  margin: 0 0 0.35rem;
}

h2 {
  margin: 0 0 0.75rem;
  font-size: 1.15rem;
}

.block {
  margin-bottom: 2rem;
}

.block-head {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 0.75rem;
}

.block-head h2 {
  margin: 0;
}

.filters {
  display: flex;
  gap: 0.4rem;
}

.table {
  width: 100%;
  border-collapse: collapse;
  background: var(--cream);
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
}

th,
td {
  text-align: left;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--border);
}

th {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--muted);
  background: var(--paper-warm);
}

.row-actions {
  display: flex;
  gap: 0.4rem;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
}

.date-select {
  font: inherit;
  padding: 0.35rem 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--cream);
}

.role {
  font-weight: 600;
  font-size: 0.9rem;
}

.role.yes {
  color: var(--success);
}

.role.no {
  color: var(--muted);
}

.status {
  text-transform: capitalize;
  font-weight: 600;
  font-size: 0.9rem;
}

.status[data-status='pending'] {
  color: var(--accent);
}

.status[data-status='approved'] {
  color: var(--success);
}

.status[data-status='removed'] {
  color: var(--danger);
}

.error {
  color: var(--danger);
}
</style>
